import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Discrete alert banner for the practitioner dashboard.
 *
 * By design:
 * - Nothing is shown during a "calm" trial period (>7 days remaining)
 * - Banner appears at trial J-7 to remind the practitioner gently
 * - Red banner for past_due (grace period) and suspended
 * - Nothing for active / exempted
 */
export default function SubscriptionAlertBanner({ token }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/practitioner/billing/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!cancelled) setInfo(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  if (!info) return null;

  const { status, days_left_trial, days_left_grace } = info;

  // Calm trial — show nothing
  if (status === "trial" && (days_left_trial === null || days_left_trial > 7)) return null;
  // Active or exempted — show nothing
  if (status === "active" || status === "exempted") return null;

  // Amber: last week of trial
  if (status === "trial" && days_left_trial !== null && days_left_trial <= 7) {
    return (
      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3" data-testid="sub-banner-trial-ending">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1 text-sm text-amber-900">
          <strong>Il vous reste {days_left_trial} jour(s)</strong> pour souscrire afin de continuer à recevoir des RDV sans interruption.
        </div>
        <Link
          to="/practitioner/billing"
          className="inline-flex items-center gap-1 text-sm font-semibold text-amber-900 hover:text-amber-700"
          data-testid="sub-banner-cta"
        >
          Souscrire <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  // Red: past_due or suspended
  if (status === "past_due" || status === "suspended" || status === "cancelled") {
    const isSuspended = status === "suspended";
    return (
      <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-3" data-testid="sub-banner-action-required">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
        <div className="flex-1 text-sm text-red-900">
          {isSuspended ? (
            <><strong>Votre profil est masqué</strong> des résultats de recherche. Souscrivez pour le réactiver.</>
          ) : status === "cancelled" ? (
            <><strong>Votre abonnement est annulé.</strong> Souscrivez à nouveau pour réactiver votre compte.</>
          ) : (
            <><strong>Essai terminé.</strong> Il vous reste {days_left_grace ?? 0} jour(s) pour souscrire avant masquage de votre profil.</>
          )}
        </div>
        <Link
          to="/practitioner/billing"
          className="inline-flex items-center gap-1 text-sm font-semibold text-red-900 hover:text-red-700"
          data-testid="sub-banner-cta"
        >
          Souscrire <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return null;
}
